/**
 * Shared responsive shell for the menu screens (title / settings / instant /
 * campaign). Every menu page caps its width at the same SCREEN_MAX_W, centres
 * itself, and shrinks-to-fit on short viewports so a menu never scrolls — the
 * treatment first written for the title screen, lifted here so it's uniform.
 *
 * The shrink-to-fit must be JS: CSS can't read content height to compute the
 * scale. We only ever touch `transform` (a uniform scale) — the layout box is
 * pinned to 100dvh so it never drives a scrollbar, and scaling the content to
 * fit avoids the feedback where a taller box lets flex children grow back and
 * re-overflow.
 */

/** Shared width / height caps for every screen — menus and the game (px). */
export const SCREEN_MAX_W = 2000;
export const SCREEN_MAX_H = 2000;

export interface FitOpts {
  /**
   * Vertically centre the content when it fits, top-anchoring + scaling only
   * when it overflows. Use for the fixed-content screens (settings/instant/
   * campaign). The title omits this — its flex layout fills the height itself.
   */
  center?: boolean;
}

/**
 * Make `el` a fitted menu screen: width:100% capped at SCREEN_MAX_W, centred,
 * pinned to the viewport height, and scaled down whenever its content is taller
 * than the viewport. Returns a cleanup fn that detaches the resize listener
 * (call it from the screen's `destroy`).
 */
export function fitScreen(el: HTMLElement, opts: FitOpts = {}): () => void {
  el.style.width = '100%';
  el.style.maxWidth = `${SCREEN_MAX_W}px`;
  el.style.minWidth = '0';
  el.style.margin = '0 auto';
  el.style.height = '100dvh';
  el.style.maxHeight = `${SCREEN_MAX_H}px`;
  el.style.transformOrigin = 'top center';
  if (opts.center) {
    el.style.display = 'flex';
    el.style.flexDirection = 'column';
  }

  const fit = (): void => {
    el.style.transform = 'none';
    // Measure the true content height top-anchored — a centred overflow would
    // hide the upper spill from scrollHeight and under-report it.
    if (opts.center) el.style.justifyContent = 'flex-start';
    const availH = window.innerHeight;
    const contentH = el.scrollHeight;
    if (contentH > availH + 0.5) {
      el.style.transform = `scale(${availH / contentH})`; // stays top-anchored
    } else if (opts.center) {
      el.style.justifyContent = 'center'; // room to spare → centre vertically
    }
  };
  fit();
  requestAnimationFrame(fit); // re-measure once layout/fonts settle
  window.addEventListener('resize', fit);
  return () => window.removeEventListener('resize', fit);
}
